// Default configuration based on legacy ProductsTable behavior
import { SelectedColumn, SortState, FilterState } from './types'
import { shouldUseSourceStoreDefaultColumns } from './linkingColumnVisibility'

export const DEFAULT_PAGE_SIZE = 25

// Default columns for Secondary/ERP/Accounting/Shipping stores (with linking features)
// NOTE: Field names match the API column catalog response
export const SECONDARY_STORE_DEFAULT_COLUMNS: SelectedColumn[] = [
  { field: 'product_name', order: 0, width: 240, pinned: null },
  { field: 'linking_status', order: 1, width: 150, pinned: null },
  { field: 'link_actions', order: 2, width: 150, pinned: null },
  { field: 'sku', order: 3, width: 140, pinned: null },
  { field: 'quantity', order: 4, width: 140, pinned: null },
  { field: 'sync_status', order: 5, width: 220, pinned: null },
  { field: 'price', order: 6, width: 100, pinned: null },
  { field: 'barcode', order: 7, width: 160, pinned: null },
  { field: 'created_at', order: 8, width: 160, pinned: null },
  { field: 'updated_at', order: 9, width: 160, pinned: null },
]

// Default columns for Primary store (without linking features)
// NOTE: Field names match the API column catalog response
export const PRIMARY_STORE_DEFAULT_COLUMNS: SelectedColumn[] = [
  { field: 'product_name', order: 0, width: 220, pinned: null },
  { field: 'api_status', order: 1, width: 130, pinned: null },
  { field: 'sku', order: 2, width: 140, pinned: null },
  { field: 'quantity', order: 3, width: 140, pinned: null },
  { field: 'price', order: 4, width: 100, pinned: null },
  { field: 'barcode', order: 5, width: 160, pinned: null },
  { field: 'created_at', order: 6, width: 160, pinned: null },
  { field: 'updated_at', order: 7, width: 160, pinned: null },
]

// Fallback for backward compatibility
export const DEFAULT_VISIBLE_COLUMNS: SelectedColumn[] = SECONDARY_STORE_DEFAULT_COLUMNS

export const DEFAULT_SORT_STATE: SortState = {
  field: 'updated_at',
  direction: 'desc',
}

export const DEFAULT_FILTER_STATE: FilterState = {
  globalSearch: '',
  columnFilters: [],
}

export const PINNED_COLUMNS = {
  left: [] as string[],
  right: [] as string[],
}

export const COLUMN_WIDTH_CONSTRAINTS = {
  min: 80,
  max: 600,
  default: 150,
}

export const getDefaultColumns = (isPrimaryStore: boolean = false): SelectedColumn[] => {
  return isPrimaryStore ? PRIMARY_STORE_DEFAULT_COLUMNS : SECONDARY_STORE_DEFAULT_COLUMNS
}

export interface DefaultColumnOptions {
  mainStore?: boolean
  accountingStore?: boolean
  shippingStore?: boolean
  erpStore?: boolean
  omsStore?: boolean
  hasLinkingColumns?: boolean
  isTiktokPlatform?: boolean
  isFairePlatform?: boolean
  isLightspeedPlatform?: boolean
  isWalmartPlatform?: boolean
  tabsData?: Array<{type?: string}>
}

export const getDefaultColumnsFromLegacyRules = (options: DefaultColumnOptions = {}): SelectedColumn[] => {
  const {
    mainStore = false,
    accountingStore = false,
    shippingStore = false,
    erpStore = false,
    omsStore = false,
    hasLinkingColumns = true,
    isTiktokPlatform = false,
    isFairePlatform = false,
    isLightspeedPlatform = false,
    isWalmartPlatform = false,
    tabsData = [],
  } = options

  const sourceStoreDefaults = shouldUseSourceStoreDefaultColumns({
    mainStore,
    accountingStore,
    shippingStore,
    erpStore,
    omsStore,
    hasLinkingColumns,
  })

  const widthMap: Record<string, number> = {
    product_name: sourceStoreDefaults ? 220 : 240,
    linking_status: 150,
    link_actions: 150,
    api_status: 130,
    sku: sourceStoreDefaults ? 140 : 120,
    api_id: 140,
    quantity: 140,
    sync_status: 220,
    accounting_sync_status: 220,
    shipping_sync_status: 220,
    erp_sync_status: 220,
    erp_secondary_sync_status: 220,
    price: 100,
    compare_price: 160,
    barcode: 160,
    gtin_number: 140,
    api_code: 140,
    created_at: 160,
    updated_at: 160,
  }

  const channelSyncFields = (tabsData || [])
    .map((item) => (item?.type === 'primary' ? 'sync_status' : `${item?.type}_sync_status`))
    .filter(Boolean)

  const withPlatformConditionalFields = (fields: string[]) => {
    const next = [...fields]
    if (isTiktokPlatform) {
      const skuIndex = next.indexOf('sku')
      if (skuIndex >= 0) next.splice(skuIndex + 1, 0, 'api_id')
      else next.push('api_id')
    }
    if (isFairePlatform) next.push('compare_price')
    if (isLightspeedPlatform) next.push('gtin_number')
    if (isWalmartPlatform) next.push('api_code')
    return next
  }

  // Legacy `columns1` branch in old table.
  if (sourceStoreDefaults) {
    const base = ['product_name', 'api_status', 'sku', 'quantity', 'price', 'barcode', 'created_at', 'updated_at']
    const ordered = withPlatformConditionalFields(base)
    return ordered.map((field, order) => ({
      field,
      order,
      width: widthMap[field] || 150,
      pinned: null,
    }))
  }

  // Legacy `columnsRange` branch in old table.
  let fields = ['product_name']

  if (hasLinkingColumns) {
    fields.push('linking_status', 'link_actions')
  }

  // Old table shows Status in columnsRange for Main/ERP context.
  if (mainStore || erpStore) {
    fields.push('api_status')
  }

  fields.push('sku', 'quantity')
  fields.push(...channelSyncFields)
  fields.push('price', 'barcode', 'created_at', 'updated_at')
  fields = withPlatformConditionalFields(fields)

  const uniqueOrdered = Array.from(new Set(fields))
  return uniqueOrdered.map((field, order) => ({
    field,
    order,
    width: widthMap[field] || 150,
    pinned: null,
  }))
}

export const getDefaultState = (isPrimaryStore: boolean = false) => ({
  selectedColumns: getDefaultColumns(isPrimaryStore),
  sortState: DEFAULT_SORT_STATE,
  filterState: DEFAULT_FILTER_STATE,
  pageSize: DEFAULT_PAGE_SIZE,
})

